import React, { useEffect, useState } from 'react';
import { ArrowUpRight, ArrowDownLeft, Wallet, ShoppingBag, Clock, CheckCircle, XCircle } from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { formatDateIST, formatTimeIST } from '../../utils/timezone';

const TransactionHistory = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [fundRequests, setFundRequests] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchAllTransactions();
  }, []);

  const fetchAllTransactions = async () => {
    try {
      const [fundRes, transferRes, orderRes] = await Promise.all([
        api.get('/wallet/my-fund-requests'),
        api.get('/wallet/transactions'),
        api.get('/orders')
      ]);

      setFundRequests(fundRes.data);
      setTransfers(transferRes.data);
      setOrders(orderRes.data);

      // Combine all transactions
      const allTransactions = [
        ...fundRes.data.map(f => ({
          ...f,
          type: 'fund_request',
          amount: f.amount,
          date: f.created_at,
          description: `Add Funds Request`,
          status: f.status
        })),
        ...transferRes.data.map(t => ({
          ...t,
          type: t.sender_id === user?.id ? 'transfer_out' : 'transfer_in',
          amount: t.amount,
          date: t.created_at,
          description: t.sender_id === user?.id 
            ? `Transfer to @${t.recipient_username}` 
            : `Received from @${t.sender_username}`,
          status: 'completed'
        })),
        ...orderRes.data.map(o => ({
          ...o,
          type: 'purchase',
          amount: o.total_amount,
          date: o.created_at,
          description: `Order #${o.id.slice(0, 8)} - ${o.items.length} item(s)`,
          status: o.payment_status
        }))
      ];

      // Sort by date (newest first)
      allTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));
      setTransactions(allTransactions);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'fund_request':
        return <Wallet className="w-5 h-5 text-primary" />;
      case 'transfer_out':
        return <ArrowUpRight className="w-5 h-5 text-red-500" />;
      case 'transfer_in':
        return <ArrowDownLeft className="w-5 h-5 text-green-500" />;
      case 'purchase':
        return <ShoppingBag className="w-5 h-5 text-accent" />;
      default:
        return <Wallet className="w-5 h-5" />;
    }
  };

  const getAmountColor = (type, status) => {
    if (status === 'rejected') return 'text-muted-foreground line-through';
    if (type === 'transfer_out' || type === 'purchase') return 'text-red-500';
    if (type === 'transfer_in' || (type === 'fund_request' && status === 'approved')) return 'text-green-500';
    return 'text-muted-foreground';
  };

  const getAmountPrefix = (type, status) => {
    if (status === 'pending' || status === 'rejected') return '';
    if (type === 'transfer_out' || type === 'purchase') return '-';
    if (type === 'transfer_in' || type === 'fund_request') return '+';
    return '';
  };

  const filteredTransactions = activeTab === 'all' 
    ? transactions 
    : transactions.filter(t => {
        if (activeTab === 'purchases') return t.type === 'purchase';
        if (activeTab === 'transfers') return t.type === 'transfer_in' || t.type === 'transfer_out';
        if (activeTab === 'funds') return t.type === 'fund_request';
        return true;
      });

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-2xl">Loading transactions...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="transaction-history-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="transactions-title">
          Transaction History
        </h1>
        
        {/* Wallet Balance Summary */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="glass-card p-4">
            <p className="text-sm text-muted-foreground mb-1">Cash Wallet</p>
            <p className="text-2xl font-bold text-primary">₹{user?.cash_wallet?.toFixed(2) || '0.00'}</p>
          </div>
          <div className="glass-card p-4">
            <p className="text-sm text-muted-foreground mb-1">Commission Wallet</p>
            <p className="text-2xl font-bold text-accent">₹{user?.commission_wallet?.toFixed(2) || '0.00'}</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'all', label: 'All' },
            { id: 'purchases', label: 'Purchases' },
            { id: 'transfers', label: 'Transfers' },
            { id: 'funds', label: 'Add Funds' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Transactions List */}
        {filteredTransactions.length > 0 ? (
          <div className="space-y-3">
            {filteredTransactions.map((tx, index) => (
              <div 
                key={tx.id || index} 
                className="glass-card p-4 flex items-center gap-4"
                data-testid={`transaction-${tx.id || index}`}
              >
                <div className="p-3 bg-white/5 rounded-full">
                  {getTypeIcon(tx.type)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{tx.description}</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>{formatDateIST(tx.date)}</span>
                    <span>•</span>
                    <span>{formatTimeIST(tx.date)} IST</span>
                  </div>
                </div>

                <div className="text-right flex items-center gap-3">
                  <div>
                    <p className={`text-lg font-bold ${getAmountColor(tx.type, tx.status)}`}>
                      {getAmountPrefix(tx.type, tx.status)}₹{tx.amount?.toFixed(2)}
                    </p>
                    <div className="flex items-center justify-end gap-1 text-xs">
                      {getStatusIcon(tx.status)}
                      <span className="capitalize">{tx.status}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 glass-card">
            <Wallet className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-2">No transactions yet</h2>
            <p className="text-muted-foreground">Your transaction history will appear here</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;
